/**
 * SQL ANSI Export utility
 * Exports database model to standard SQL DDL
 */

/**
 * Escape SQL identifiers (table/column names)
 */
const escapeIdentifier = (name) => {
  return name.replace(/"/g, '');
};

/**
 * Escape SQL string literals
 */
const escapeString = (str) => {
  return `'${str.replace(/'/g, "''")}'`;
};

/**
 * Resolve UDT references to their base type.
 * `UDT:TypeName` → baseType stored in userTypes array.
 */
const resolveUdt = (type, userTypes = []) => {
  if (!type?.startsWith('UDT:')) return type;
  const udtName = type.slice(4);
  const udt = userTypes.find(u => u.name === udtName);
  return udt ? udt.baseType : udtName;
};

/**
 * Map internal data types to SQL ANSI types
 */
const mapDataType = (type, userTypes = []) => {
  const resolved = resolveUdt(type, userTypes);
  const typeMap = {
    // Numeric types
    'INTEGER': 'INTEGER',
    'BIGINT': 'BIGINT',
    'SMALLINT': 'SMALLINT',
    'DECIMAL': 'DECIMAL',
    'NUMERIC': 'NUMERIC',
    'REAL': 'REAL',
    'DOUBLE PRECISION': 'DOUBLE PRECISION',
    'FLOAT': 'FLOAT',
    
    // Character types
    'VARCHAR': 'VARCHAR',
    'CHAR': 'CHAR',
    'TEXT': 'TEXT',
    
    // Date/Time types
    'DATE': 'DATE',
    'TIME': 'TIME',
    'TIMESTAMP': 'TIMESTAMP',
    'TIMESTAMPTZ': 'TIMESTAMP WITH TIME ZONE',
    
    // Boolean
    'BOOLEAN': 'BOOLEAN',
    'BOOL': 'BOOLEAN',
    
    // Binary
    'BYTEA': 'BYTEA',
    'BLOB': 'BLOB',
    
    // JSON
    'JSON': 'JSON',
    'JSONB': 'JSON', // JSONB is PostgreSQL specific, map to JSON for ANSI
    
    // Other
    'UUID': 'UUID',
    'XML': 'XML',
    'GEOMETRY': 'GEOMETRY',
    'GEOGRAPHY': 'GEOGRAPHY'
  };
  
  const upperType = resolved.toUpperCase().trim();
  return typeMap[upperType] || upperType;
};

/**
 * Generate comment SQL for text boxes
 */
const generateTextBoxComments = (nodes) => {
  const textBoxes = nodes.filter(node => node.type === 'text');
  if (textBoxes.length === 0) return '';
  
  let sql = '-- ========================================\n';
  sql += '-- Model Documentation\n';
  sql += '-- ========================================\n\n';
  
  textBoxes.forEach(textBox => {
    const content = textBox.data?.content || '';
    const lines = content.split('\n');
    lines.forEach(line => {
      sql += `-- ${line}\n`;
    });
    sql += '\n';
  });
  
  return sql;
};

/**
 * Get full entity name including schema if specified
 */
const getFullEntityName = (node) => {
  const name = node.data?.name || 'unnamed';
  const schema = node.data?.schema?.trim();
  return schema ? `${escapeIdentifier(schema)}.${escapeIdentifier(name)}` : escapeIdentifier(name);
};

/**
 * Generate CREATE TABLE statement
 */
const generateCreateTable = (node, userTypes = []) => {
  const tableName = node.data?.name || 'unnamed_table';
  const fullTableName = getFullEntityName(node);
  const fields = node.data?.fields || [];
  const description = node.data?.description;
  const isMaterialized = node.data?.materialized || false;
  const isView = node.type === 'view';
  
  let sql = '';
  
  // Table/View comment
  if (description) {
    sql += `-- ${description}\n`;
  }
  
  // For views, we'll just add a placeholder (views are created later)
  if (isView) {
    sql += `-- VIEW: ${fullTableName} (defined below)\n\n`;
    return sql;
  }
  
  sql += `CREATE TABLE ${fullTableName} (\n`;
  
  const columnDefs = [];
  const pkFields = [];
  
  // Generate column definitions
  fields.forEach(field => {
    let colDef = `  ${escapeIdentifier(field.name)} ${mapDataType(field.type, userTypes)}`;
    
    // Nullable
    if (!field.nullable) {
      colDef += ' NOT NULL';
    }
    
    // Auto-increment (SERIAL for PostgreSQL-like, but we'll use standard approach)
    if (field.autoIncrement) {
      colDef += ' GENERATED ALWAYS AS IDENTITY';
    }

    // CHECK constraint
    if (field.checkConstraint && field.checkConstraint.trim()) {
      const checkExpr = field.checkConstraint.trim();
      const formattedCheck = checkExpr.toUpperCase().startsWith('CHECK')
        ? checkExpr
        : `CHECK (${checkExpr})`;
      colDef += ` ${formattedCheck}`;
    }
    
    // Collect PK fields for constraint
    if (field.isPrimaryKey) {
      pkFields.push(field.name);
    }
    
    // Add comment if exists
    if (field.description) {
      colDef += ` -- ${field.description}`;
    }
    
    columnDefs.push(colDef);
  });
  
  // Add PRIMARY KEY constraint if exists
  if (pkFields.length > 0) {
    const pkDef = `  CONSTRAINT ${escapeIdentifier(`pk_${tableName}`)} PRIMARY KEY (${pkFields.map(escapeIdentifier).join(', ')})`;
    columnDefs.push(pkDef);
  }
  
  sql += columnDefs.join(',\n');
  sql += '\n)';

  // Add Table Partitioning clause if defined
  const partitioning = node.data?.partitioning;
  if (partitioning && partitioning.type && partitioning.type !== 'none') {
    if (partitioning.type === 'range') {
      const col = escapeIdentifier(partitioning.column || (fields[0]?.name || 'id'));
      sql += `\nPARTITION BY RANGE (${col})`;
      const parts = partitioning.partitions || [];
      if (parts.length > 0) {
        sql += ' (\n';
        const partDefs = parts.map(part => {
          const pName = escapeIdentifier(part.name || 'p');
          if (part.from && part.to) {
            return `  PARTITION ${pName} VALUES FROM (${escapeString(part.from)}) TO (${escapeString(part.to)})`;
          } else if (part.to) {
            return `  PARTITION ${pName} VALUES LESS THAN (${escapeString(part.to)})`;
          } else if (part.from) {
            return `  PARTITION ${pName} VALUES FROM (${escapeString(part.from)}) TO (MAXVALUE)`;
          } else {
            return `  PARTITION ${pName}`;
          }
        });
        sql += partDefs.join(',\n') + '\n)';
      }
    } else if (partitioning.type === 'list') {
      const col = escapeIdentifier(partitioning.column || (fields[0]?.name || 'id'));
      sql += `\nPARTITION BY LIST (${col})`;
      const parts = partitioning.partitions || [];
      if (parts.length > 0) {
        sql += ' (\n';
        const partDefs = parts.map(part => {
          const pName = escapeIdentifier(part.name || 'p');
          if (part.values && part.values.trim()) {
            return `  PARTITION ${pName} VALUES IN (${part.values.trim()})`;
          } else {
            return `  PARTITION ${pName} VALUES IN (DEFAULT)`;
          }
        });
        sql += partDefs.join(',\n') + '\n)';
      }
    } else if (partitioning.type === 'hash') {
      const cols = (partitioning.columns && partitioning.columns.length > 0)
        ? partitioning.columns.map(escapeIdentifier).join(', ')
        : escapeIdentifier(fields[0]?.name || 'id');
      const count = partitioning.hashPartitionsCount || 4;
      sql += `\nPARTITION BY HASH (${cols})\nPARTITIONS ${count}`;
    }
  }

  sql += ';\n\n';

  // Add table comment (description only)
  if (description) {
    sql += `COMMENT ON TABLE ${fullTableName} IS ${escapeString(description)};\n`;
  }
  
  // Add column comments (description only)
  fields.forEach(field => {
    if (field.description) {
      sql += `COMMENT ON COLUMN ${fullTableName}.${escapeIdentifier(field.name)} IS ${escapeString(field.description)};\n`;
    }
  });
  
  if (description || fields.some(f => f.description)) {
    sql += '\n';
  }
  
  // Add business rules as SQL comments
  const businessRules = node.data?.businessRules;
  
  if (businessRules?.entity) {
    sql += `/* ========================================\n`;
    sql += ` * BUSINESS RULES - ${tableName}\n`;
    sql += ` * ========================================\n`;
    businessRules.entity.split('\n').forEach(line => {
      sql += ` * ${line}\n`;
    });
    sql += ` * ========================================\n`;
    sql += ` */\n\n`;
  }
  
  // Add field-level business rules as SQL comments
  const fieldRules = businessRules?.fields || {};
  const fieldsWithRules = fields.filter(field => fieldRules[field.name]);
  
  if (fieldsWithRules.length > 0) {
    sql += `/* ----------------------------------------\n`;
    sql += ` * FIELD BUSINESS RULES - ${tableName}\n`;
    sql += ` * ----------------------------------------\n`;
    fieldsWithRules.forEach(field => {
      sql += ` *\n`;
      sql += ` * Field: ${field.name}\n`;
      fieldRules[field.name].split('\n').forEach(line => {
        sql += ` * ${line}\n`;
      });
    });
    sql += ` * ----------------------------------------\n`;
    sql += ` */\n\n`;
  }
  
  return sql;
};

/**
 * Generate CREATE INDEX statements for a table
 */
const generateIndexes = (node) => {
  const fullTableName = getFullEntityName(node);
  const indexes = node.data?.indexes || [];
  
  if (indexes.length === 0) return '';
  
  let sql = '';
  
  indexes.forEach(index => {
    if (!index.name || !index.columns || index.columns.length === 0) return;
    
    const indexName = index.name;
    const isUnique = index.type === 'Unique';
    const columns = index.columns.map(col => {
      const colName = escapeIdentifier(col.fieldName);
      const order = col.order || 'ASC';
      return `${colName} ${order}`;
    }).join(', ');
    
    sql += `CREATE ${isUnique ? 'UNIQUE ' : ''}INDEX ${escapeIdentifier(indexName)} ON ${fullTableName} (${columns});\n`;
  });
  
  if (sql) {
    sql += '\n';
  }
  
  return sql;
};

/**
 * Generate CREATE TABLE for junction tables (N:N relationships)
 */
const generateJunctionTables = (edges, userTypes = []) => {
  if (!edges || edges.length === 0) return '';
  
  let sql = '';
  
  edges.forEach(edge => {
    const cardinality = edge.data?.cardinality;
    const junctionTable = edge.data?.junctionTable;
    
    // Only process N:N relationships with junction tables
    if (cardinality !== 'N:N' || !junctionTable) return;
    
    const tableName = junctionTable.name;
    const fields = junctionTable.fields || [];
    const description = junctionTable.description;
    
    if (fields.length === 0) return;
    
    if (description) {
      sql += `-- ${description}\n`;
    }
    
    sql += `CREATE TABLE ${escapeIdentifier(tableName)} (\n`;
    
    const columnDefs = [];
    const pkFields = [];
    
    // Generate column definitions
    fields.forEach(field => {
      let colDef = `  ${escapeIdentifier(field.name)} ${mapDataType(field.type, userTypes)}`;
      
      // NOT NULL before GENERATED
      if (!field.nullable) {
        colDef += ' NOT NULL';
      }
      
      // Auto-increment
      if (field.autoIncrement) {
        colDef += ' GENERATED ALWAYS AS IDENTITY';
      }
      
      // Collect PK fields for constraint
      if (field.isPrimaryKey) {
        pkFields.push(field.name);
      }
      
      // Add comment if exists
      if (field.description) {
        colDef += ` -- ${field.description}`;
      }
      
      columnDefs.push(colDef);
    });
    
    // Add PRIMARY KEY constraint if exists
    if (pkFields.length > 0) {
      const pkDef = `  CONSTRAINT ${escapeIdentifier(`pk_${tableName}`)} PRIMARY KEY (${pkFields.map(escapeIdentifier).join(', ')})`;
      columnDefs.push(pkDef);
    }
    
    sql += columnDefs.join(',\n');
    sql += '\n);\n\n';
    
    // Add table comment (description only for junction tables - no business rules expected)
    if (description) {
      sql += `COMMENT ON TABLE ${escapeIdentifier(tableName)} IS ${escapeString(description)};\n`;
    }
    
    // Add column comments
    fields.forEach(field => {
      if (field.description) {
        sql += `COMMENT ON COLUMN ${escapeIdentifier(tableName)}.${escapeIdentifier(field.name)} IS ${escapeString(field.description)};\n`;
      }
    });
    
    if (fields.some(f => f.description)) {
      sql += '\n';
    }
    
    // Add indexes for junction table
    const indexes = junctionTable.indexes || [];
    indexes.forEach(index => {
      if (!index.name || !index.columns || index.columns.length === 0) return;
      
      const indexName = index.name;
      const isUnique = index.type === 'Unique';
      const columns = index.columns.map(col => {
        const colName = escapeIdentifier(col.fieldName);
        const order = col.order || 'ASC';
        return `${colName} ${order}`;
      }).join(', ');
      
      sql += `CREATE ${isUnique ? 'UNIQUE ' : ''}INDEX ${escapeIdentifier(indexName)} ON ${escapeIdentifier(tableName)} (${columns});\n`;
    });
    
    if (indexes.length > 0) {
      sql += '\n';
    }
  });
  
  return sql;
};

/**
 * Generate ALTER TABLE statements for foreign keys (including junction tables)
 */
const generateForeignKeys = (nodes, edges) => {
  if (!edges || edges.length === 0) return '';
  
  let sql = '';
  
  edges.forEach((edge, index) => {
    const cardinality = edge.data?.cardinality;
    
    // Handle N:N relationships with junction tables
    if (cardinality === 'N:N') {
      const junctionTable = edge.data?.junctionTable;
      if (!junctionTable) return;
      
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      
      if (!sourceNode || !targetNode) return;
      
      const sourceTable = sourceNode.data?.name;
      const targetTable = targetNode.data?.name;
      const junctionTableName = junctionTable.name;
      
      const sourcePK = junctionTable.sourcePK || 'id';
      const targetPK = junctionTable.targetPK || 'id';
      
      // FK from junction table to source table
      const sourceFKField = `${sourceTable}_${sourcePK}`;
      const sourceFKName = `fk_${junctionTableName}_${sourceTable}`;
      
      sql += `ALTER TABLE ${escapeIdentifier(junctionTableName)}\n`;
      sql += `  ADD CONSTRAINT ${escapeIdentifier(sourceFKName)}\n`;
      sql += `  FOREIGN KEY (${escapeIdentifier(sourceFKField)})\n`;
      sql += `  REFERENCES ${escapeIdentifier(sourceTable)} (${escapeIdentifier(sourcePK)});\n\n`;
      
      // FK from junction table to target table
      const targetFKField = `${targetTable}_${targetPK}`;
      const targetFKName = `fk_${junctionTableName}_${targetTable}`;
      
      sql += `ALTER TABLE ${escapeIdentifier(junctionTableName)}\n`;
      sql += `  ADD CONSTRAINT ${escapeIdentifier(targetFKName)}\n`;
      sql += `  FOREIGN KEY (${escapeIdentifier(targetFKField)})\n`;
      sql += `  REFERENCES ${escapeIdentifier(targetTable)} (${escapeIdentifier(targetPK)});\n\n`;
      
      return;
    }
    
    // Handle other cardinalities (1:1, 1:N, N:1)
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);
    
    if (!sourceNode || !targetNode) return;
    
    const sourceTable = sourceNode.data?.name;
    const targetTable = targetNode.data?.name;
    
    if (!sourceTable || !targetTable) return;
    
    const sourceFull = getFullEntityName(sourceNode);
    const targetFull = getFullEntityName(targetNode);
    
    // Find PK of source table
    const sourcePK = sourceNode.data?.fields?.find(f => f.isPrimaryKey);
    if (!sourcePK) return;
    
    // Determine FK field name (typically source_table_id or source_pk_name)
    const fkFieldName = `${sourceTable}_${sourcePK.name}`;
    const fkName = `fk_${targetTable}_${sourceTable}_${index}`;
    
    sql += `ALTER TABLE ${targetFull}\n`;
    sql += `  ADD CONSTRAINT ${escapeIdentifier(fkName)}\n`;
    sql += `  FOREIGN KEY (${escapeIdentifier(fkFieldName)})\n`;
    sql += `  REFERENCES ${sourceFull} (${escapeIdentifier(sourcePK.name)});\n\n`;
  });
  
  return sql;
};

/**
 * Generate CREATE VIEW statements
 */
const generateViews = (nodes) => {
  const views = nodes.filter(node => node.type === 'view');
  
  if (views.length === 0) return '';
  
  let sql = '';
  
  views.forEach(view => {
    const viewName = view.data?.name || 'unnamed_view';
    const fullViewName = getFullEntityName(view);
    const description = view.data?.description;
    const isMaterialized = view.data?.materialized || false;
    
    if (description) {
      sql += `-- ${description}\n`;
    }
    
    // For materialized views
    if (isMaterialized) {
      sql += `CREATE MATERIALIZED VIEW ${fullViewName} AS\n`;
    } else {
      sql += `CREATE VIEW ${fullViewName} AS\n`;
    }
    
    sql += `  SELECT -- Define your view query here\n`;
    sql += `    *\n`;
    sql += `  FROM -- source_table;\n\n`;
    
    // Add view comment (description only)
    if (description) {
      sql += `COMMENT ON VIEW ${escapeIdentifier(viewName)} IS ${escapeString(description)};\n\n`;
    }
    
    // Add business rules as SQL comments
    const businessRules = view.data?.businessRules;
    
    if (businessRules?.entity) {
      sql += `/* ========================================\n`;
      sql += ` * BUSINESS RULES - ${viewName} (VIEW)\n`;
      sql += ` * ========================================\n`;
      businessRules.entity.split('\n').forEach(line => {
        sql += ` * ${line}\n`;
      });
      sql += ` * ========================================\n`;
      sql += ` */\n\n`;
    }
  });
  
  return sql;
};

/**
 * Main export function
 */
/**
 * Strip Markdown syntax for plain-text SQL comments.
 * Removes headings, bold, italic, inline code, and link syntax.
 */
const stripMarkdown = (text) => {
  return text
    .replace(/^#{1,6}\s+/gm, '')          // headings
    .replace(/\*\*(.+?)\*\*/g, '$1')       // bold
    .replace(/\*(.+?)\*/g, '$1')           // italic *
    .replace(/__(.+?)__/g, '$1')           // bold __
    .replace(/_(.+?)_/g, '$1')             // italic _
    .replace(/`(.+?)`/g, '$1')             // inline code
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')    // links
    .replace(/^[-*+]\s+/gm, '- ')         // unordered list markers (normalise)
    .trim();
};

export const exportToSQL = (modelData) => {
  const nodes = modelData.nodes || [];
  const edges = modelData.edges || [];
  const modelName = modelData.name || 'Untitled Model';
  const modelType = modelData.type || 'relational';
  const userTypes = modelData.userTypes || [];
  const modelBusinessRules = modelData.businessRules || '';
  
  let sql = '';
  
  // Header
  sql += '-- ========================================\n';
  sql += `-- SQL DDL Export: ${modelName}\n`;
  sql += `-- Model Type: ${modelType}\n`;
  sql += `-- Generated: ${new Date().toISOString()}\n`;
  sql += `-- Generated by: SchemaGraphos\n`;
  sql += '-- ========================================\n\n';

  // Model-level Business Rules (plain-text, no Markdown)
  if (modelBusinessRules.trim()) {
    sql += '-- ========================================\n';
    sql += '-- Business Rules\n';
    sql += '-- ========================================\n';
    stripMarkdown(modelBusinessRules).split('\n').forEach(line => {
      sql += `-- ${line}\n`;
    });
    sql += '-- ========================================\n\n';
  }

  // Text box comments (documentation)
  const textBoxComments = generateTextBoxComments(nodes);
  if (textBoxComments) {
    sql += textBoxComments;
  }

  // User-Defined Types section (informational comment block)
  const usedUdtNames = new Set();
  nodes.forEach(node => {
    (node.data?.fields || []).forEach(f => {
      if (f.type?.startsWith('UDT:')) usedUdtNames.add(f.type.slice(4));
    });
  });
  if (usedUdtNames.size > 0) {
    sql += '-- ========================================\n';
    sql += '-- User-Defined Types used in this model\n';
    sql += '-- (resolved to base SQL types in DDL below)\n';
    sql += '-- ========================================\n';
    usedUdtNames.forEach(name => {
      const udt = userTypes.find(u => u.name === name);
      if (udt) {
        sql += `-- UDT:${udt.name}  →  ${udt.baseType}${udt.size ? `(${udt.size})` : ''}\n`;
        if (udt.fillRule)       sql += `--   Fill Rule       : ${udt.fillRule}\n`;
        if (udt.validationRule) sql += `--   Validation Rule : ${udt.validationRule}\n`;
      }
    });
    sql += '\n';
  }
  
  // Create Schema statements
  const schemas = Array.from(new Set(nodes.map(n => n.data?.schema?.trim()).filter(Boolean))).sort();
  if (schemas.length > 0) {
    sql += '-- ========================================\n';
    sql += '-- Schemas\n';
    sql += '-- ========================================\n\n';
    schemas.forEach(sch => {
      sql += `CREATE SCHEMA IF NOT EXISTS ${escapeIdentifier(sch)};\n`;
    });
    sql += '\n';
  }

  // Separate nodes by type
  const tables = nodes.filter(node => 
    node.type === 'table' || node.type === 'fact' || node.type === 'dimension'
  );
  
  // 1. CREATE TABLE statements
  if (tables.length > 0) {
    sql += '-- ========================================\n';
    sql += '-- Tables\n';
    sql += '-- ========================================\n\n';
    
    tables.forEach(table => {
      sql += generateCreateTable(table, userTypes);
    });
  }
  
  // 2. CREATE INDEX statements
  const tablesWithIndexes = tables.filter(table => 
    table.data?.indexes && table.data.indexes.length > 0
  );
  
  if (tablesWithIndexes.length > 0) {
    sql += '-- ========================================\n';
    sql += '-- Indexes\n';
    sql += '-- ========================================\n\n';
    
    tablesWithIndexes.forEach(table => {
      sql += generateIndexes(table);
    });
  }
  
  // 3. Junction Tables for N:N relationships
  const junctionSQL = generateJunctionTables(edges, userTypes);
  if (junctionSQL) {
    sql += '-- ========================================\n';
    sql += '-- Junction Tables (N:N Relationships)\n';
    sql += '-- ========================================\n\n';
    sql += junctionSQL;
  }
  
  // 4. ALTER TABLE for Foreign Keys
  if (edges.length > 0) {
    sql += '-- ========================================\n';
    sql += '-- Foreign Keys\n';
    sql += '-- ========================================\n\n';
    
    const fkSQL = generateForeignKeys(nodes, edges);
    if (fkSQL) {
      sql += fkSQL;
    } else {
      sql += '-- No foreign keys to create\n\n';
    }
  }
  
  // 5. CREATE VIEW statements
  const viewSQL = generateViews(nodes);
  if (viewSQL) {
    sql += '-- ========================================\n';
    sql += '-- Views\n';
    sql += '-- ========================================\n\n';
    sql += viewSQL;
  }
  
  // Footer
  sql += '-- ========================================\n';
  sql += '-- End of SQL DDL Export\n';
  sql += '-- ========================================\n';
  
  return sql;
};

/**
 * Download SQL file
 */
export const downloadSQL = (modelData, filename = 'model.sql') => {
  const sql = exportToSQL(modelData);
  const blob = new Blob([sql], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
